import { QueryClient } from '@tanstack/react-query';
import { hasDetailMessages, transformAndDisplay } from '../helpers/transformErrorResponse';
import handleErrorAndDisplay from '../helpers/handleErrorAndDisplay';

/**
 * Query keys for consistent cache management
 */
export const queryKeys = {
    userMe: ['user', 'me'],
    projects: ['projects'],
    project: (id) => ['project', id],
    projectTeam: (id) => ['project', id, 'team'],
    cueSheet: (id) => ['project', id, 'cue-sheet'],
    videos: (id) => ['project', id, 'videos'],
    clips: (id) => ['clip', id],
    timeline: (id) => ['project', id, 'timeline'],
    projectMessages: (id, type, pageId = 0) => ['project', id, 'messages', type, pageId],
    recSessions: (id) => ['project', id, 'rec-sessions'],
};

/**
 * Global error handler for React Query
 * Reuses existing error handling helpers
 */
const handleQueryError = (error) => {
    // Silently ignore 403 errors that were handled with redirect
    if (error?.handled) {
        return;
    }

    const response = error?.data || error?.response?.data || error;

    // Check if response has detail messages array
    if (hasDetailMessages(response)) {
        transformAndDisplay(handleErrorAndDisplay, response);
    } else if (typeof response === 'string') {
        handleErrorAndDisplay(response);
    } else if (error?.message) {
        handleErrorAndDisplay(error.message);
    } else {
        handleErrorAndDisplay('An unexpected error occurred');
    }
};

/**
 * QueryClient configuration
 */
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
            retry: (failureCount, error) => {
                // Don't retry on 4xx errors
                const status = error?.status || error?.response?.status;
                if (status >= 400 && status < 500) {
                    return false;
                }
                // Retry up to 2 times for other errors
                return failureCount < 2;
            },
            refetchOnWindowFocus: false,
        },
        mutations: {
            onError: handleQueryError,
        },
    },
});
